import React from 'react';
import './../ff001-starstruck.css';
import { wrap, range, getSymbolCountsFromWindow, generateWindowFromReelStrips } from './../../../helpers';
import { Reel } from './../../reel/reel';
import { ICoin, IScenarioDB } from './../interfaces';
import * as coinHelpers from './../coin-spins/helpers';
import CoinSpins from './../coin-spins/coin-spins';
import { BigWin } from './../../big-win/big-win';

function Spin(props: { config: any; scenario: IScenarioDB, index: number; isLastSpin: boolean; onScenarioUpdated: (scenario: IScenarioDB, i: number) => unknown }) {
  const [config] = React.useState(props.config);
  const [window, setWindow] = React.useState<string[][]>([]); 

  const reelsCount = config?.reelsSetup?.length ?? 0;
  const normalizeReelIndexes = (value: unknown) => {
    const arr = Array.isArray(value) ? (value as unknown[]) : [];
    const normalized = Array.from({ length: reelsCount }, (_, i) => {
      const v = arr[i];
      return (typeof v === 'number' && Number.isFinite(v)) ? v : 0;
    });
    return normalized;
  };

  const reelIndexes = normalizeReelIndexes(props.scenario?.reelIndexes);
  const reelIndexesKey = reelIndexes.join(',');

  const updateScenario = (params: Partial<IScenarioDB>) => {
    const newScenario = {...props.scenario, ...params};
    props.onScenarioUpdated(newScenario, props.index);
  }
  const setWildMultiplier = (reelIndex: number, rowIndex: number, value: number) => {
    const isBase = props.index === 0;
    const wildMultConfig = props.config.wildMultipliers;
    if (!wildMultConfig) return;
    
    const options = isBase ? wildMultConfig.base?.values : wildMultConfig.freespins?.values;
    if (!options || options.length === 0) return;
    
    const rows = config.reelsSetup[0];
    const reels = config.reelsSetup.length;
    
    // Initialize as proper 2D array if needed
    const newMultipliers: number[][] = [];
    for (let r = 0; r < reels; r++) {
      newMultipliers[r] = [];
      for (let row = 0; row < rows; row++) {
        const existing = props.scenario.wildMultipliers?.[r]?.[row];
        newMultipliers[r][row] = existing !== undefined ? existing : options[0];
      }
    }
    
    newMultipliers[reelIndex][rowIndex] = value;
    updateScenario({ wildMultipliers: newMultipliers });
  }
  const setReelIndex = (params: {reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => {
    const { reelIndex, dir, forcedIndex } = params;
    const currentIndexes = reelIndexes;
    const reels = config.baseSpinsReels[reelIndex];
    const n = (typeof forcedIndex === 'number') ? forcedIndex : currentIndexes[reelIndex] + (1 * dir!);
    const c = wrap(n, reels);
    const w = c < 0 ? (reels.length + c) : c;
    updateScenario({reelIndexes: currentIndexes.map((x, i) => i === reelIndex ? w : x) });
  } 
  const onCoinSpinsWindowsUpdated = (coinSpins: (ICoin | null)[][][]) => {
    updateScenario({otherData: { coinSpins, coinSpinIndex: 0}});
  }
  React.useEffect(() => {
    const t = setTimeout(() => {
      const w = getSymbolCountsFromWindow(window);
      const coin = w['coin'] ?? 0;
      const triggerCount = config.coinFeature.coinTrigger ?? 100000; if (triggerCount===100000) { throw new Error('CoinTrigger isnt found the config')}
      if (coin >= triggerCount ) {
        if (coinHelpers.coinWindowsMatch(window, props.scenario?.otherData?.coinSpins?.[0])) { return; }
        console.log(`creating new coin spins game coins: ${coin} spins: ${config.coinFeature.coinTriggerSpinCount}`);
        const coins = coinHelpers.getCoins(props.config);
        const firstWindow = window.map((reel, ri) => reel.map((col, ci) => col === 'coin' ? {...coins[0].coin, startWindowIndex: 0} : null));
        const cs = range(config.coinFeature.coinTriggerSpinCount + 1).map((i) => { // 3 spins
          if (i === 0) { return firstWindow };
          return coinHelpers.copyWindow(firstWindow);
        });
        onCoinSpinsWindowsUpdated(cs);
      } else {
        onCoinSpinsWindowsUpdated([]);
      } 
    }, 250);
    return () => { clearTimeout(t); }
  }, [window]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    // Backwards compatibility: older saved scenarios might omit reelIndexes.
    const existing = props.scenario?.reelIndexes;
    const normalized = normalizeReelIndexes(existing);
    if (!Array.isArray(existing) || existing.length !== normalized.length || normalized.some((v, i) => existing[i] !== v)) {
      updateScenario({ reelIndexes: normalized });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reelsCount]);

  React.useEffect(() => {
    const reels = config.reelsSetup.length;
    const rows = config.reelsSetup[0];
    const w = generateWindowFromReelStrips({
      reels,
      rows,
      reelIndexes,
      reelStrips: props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels,
    });
    setWindow(w);
    
    // Initialize wildMultipliers with default values if not set
    const wildMultConfig = props.config.wildMultipliers;
    if (wildMultConfig) {
      const isBase = props.index === 0;
      const options = isBase ? wildMultConfig.base?.values : wildMultConfig.freespins?.values;
      const hasWilds = w.some(reel => reel.some(symbol => symbol === 'wild'));
      
      if (options && options.length > 0 && hasWilds && (!props.scenario.wildMultipliers || props.scenario.wildMultipliers.length === 0)) {
        const newMultipliers: number[][] = [];
        for (let r = 0; r < reels; r++) {
          newMultipliers[r] = [];
          for (let row = 0; row < rows; row++) {
            newMultipliers[r][row] = options[0];
          }
        }
        updateScenario({ wildMultipliers: newMultipliers });
      }
    }
  }, [reelIndexesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className={`spin ${props.index > 0 ? 'spin-freespins' : ''}`}>
    <div className='spin-index-num'>{props.index}</div>
    <BigWin
      config={config}
      reelStrips={props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels}
      rowsByReel={config.reelsSetup}
      reelIndexes={reelIndexes}
      mode={props.index > 0 ? 'Free Spins' : 'Base'}
      showFreespins={props.index === 0}
      onApply={nextReelIndexes => updateScenario({ reelIndexes: nextReelIndexes })}
    />
    <div className={'reelContainer'}>
      
      {reelIndexes.map((ri, i) => <Reel 
        reelIndex={ri} 
        setReelIndex={ (props: {reelIndex: number, dir?: 1 | -1, forcedIndex?: number }) => setReelIndex(props) } 
        reelStrips={(props.index > 0 ? config.freeSpinsReels : config.baseSpinsReels)[i]} 
        index={i} 
        rows={4} 
        key={`reel-${props.index}-${i}`}/>)}
    </div>
    
    <div className={'reelContainer'} style={{marginTop: 5}}>
       {props.config.wildMultipliers && window.map((reel, ri) => (
         <div key={`wm-col-${ri}`} style={{ width: 50, margin: '0 10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {reel.map((symbol, rowI) => {
               if (symbol !== 'wild') return null;
               
               const isBase = props.index === 0;
               const wildMultConfig = props.config.wildMultipliers;
               const options = isBase ? wildMultConfig.base?.values : wildMultConfig.freespins?.values;
               if (!options || options.length === 0) return null;
               
               const val = props.scenario.wildMultipliers?.[ri]?.[rowI] || options[0];
               
               return (
                 <div key={`wm-sel-${ri}-${rowI}`} style={{ marginTop: 2, marginBottom: 2 }}>
                   <div style={{fontSize: 8, textAlign: 'center', color: '#aaa'}}>Row {rowI}</div>
                   <select 
                      style={{ fontSize: 10, width: 45 }}
                      value={val}
                      onChange={e => setWildMultiplier(ri, rowI, parseInt(e.target.value))}
                   >
                      {options.map((o: number) => <option key={o} value={o}>{o}x</option>)}
                   </select>
                 </div>
               )
            })}
         </div>
       ))}
    </div>
       
    <CoinSpins window={window} coinSpinsWindow={props.scenario?.otherData?.coinSpins || []} onCoinSpinsWindowsUpdated={onCoinSpinsWindowsUpdated} config={props.config} />
  
  </div>
}
export default Spin;
